import React, { useState, useEffect, lazy, Suspense, useRef } from "react";
import { gsap } from "gsap";

// Libraries
import { Col, Container, Navbar, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { m } from "framer-motion";
import { Link as ScrollTo } from "react-scroll";
import { TypeAnimation } from "react-type-animation";
import { initCookieConsent } from "./cookieConsent";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useLanguage } from "./hooks/useLanguage";

// Components
import Header, {
  HeaderNav,
  Menu,
  MobileMenu,
} from "./Components/Header/Header";
import { fadeIn } from "./Functions/GlobalAnimations";
import Buttons from "./Components/Button/Buttons";
import AstronautImage from "./Components/AstronautImage";
import Footer from "./Components/Footer";
import VideoBackground from "./Components/VideoBackground";
import ModalPopupContact from "./Components/ModalPopupContact";
import Preloader from "./Components/Preloader";
import InViewPort from "./Components/InViewPort";
import LanguageSwitcher from "./Components/LanguageSwitcher/LanguageSwitcher";
// Data
import { generateServiceData } from "./Components/Services/ServicesData";
import { generateHeaderData } from "./Components/Header/HeaderDataGenerator";
import { generateHeaderDataMobile } from "./Components/Header/HeaderDataMobileGenerator";

// Sanity integration
import { useSanityBlogPosts } from "./utils/useSanityBlogPosts";
import { transformSanityToHomepageFormat } from "./utils/transformBlockData";

// Contact obfuscation
import { INAQ_CONTACTS } from "./utils/ContactObfuscator";

// Lazy Loaded Components
const LazyServices = lazy(() => import("./Components/Services/Services"));
import BlogOverlayImg from "./Components/Blogs/BlogOverlayImg";
const LazyCounter = lazy(() => import("./Components/Counters/Counter"));
const LazyJobs = lazy(() => import("./Components/Jobs"));
const LazySubscribeForm = lazy(() => import("./Components/SubscribeForm"));
const LazyFancyTextBox = lazy(
  () => import("./Components/FancyTextBox/FancyTextBox")
);

const generateJobData = (t) => [
  // {
  //   title: t('homepage:jobs.reactDev.title'),
  //   subtitle: t('homepage:jobs.reactDev.subtitle'),
  //   content: t('homepage:jobs.reactDev.content'),
  //   img: "/assets/img/webp/react_dev_job_image.webp",
  //   link: INAQ_CONTACTS.CAREERS_EMAIL, // Protected
  //   linkTitle: t('homepage:jobs.moreInfo'),
  //   isProtectedEmail: true,
  // },
  {
    title: t('homepage:jobs.aiConsultant.title'),
    subtitle: t('homepage:jobs.aiConsultant.subtitle'),
    content: t('homepage:jobs.aiConsultant.content'),
    img: "/assets/img/webp/ai_consultant_job_image.webp",
    link: INAQ_CONTACTS.CAREERS_EMAIL, // Protected
    linkTitle: t('homepage:jobs.moreInfo'),
    isProtectedEmail: true,
  },
];
const generateContactData = (t) => [
  {
    id: "napiste-section",
    icon: "/assets/img/mail_inaq.svg",
    title: t('homepage:contact.write.title'),
    description: t('homepage:contact.write.description'),
    actionText: INAQ_CONTACTS.MARKETING_EMAIL, // Protected (encoded)
    actionLink: INAQ_CONTACTS.MARKETING_EMAIL, // Protected (encoded)
    isProtectedEmail: true, // Flag to indicate this needs protection
  },
  {
    id: "zavolajte-section",
    icon: "/assets/img/call_inaq.svg",
    title: t('homepage:contact.call.title'),
    description: t('homepage:contact.call.description'),
    actionText: INAQ_CONTACTS.PHONE_DISPLAY, // Protected (encoded)
    actionLink: INAQ_CONTACTS.PHONE_TEL, // Protected (encoded)
    isProtectedPhone: true, // Flag to indicate this needs protection
  },
  {
    id: "vyplnte-section",
    icon: "/assets/img/form_inaq.svg",
    title: t('homepage:contact.form.title'),
    description: t('homepage:contact.form.description'),
    actionText: t('homepage:contact.form.action'),
    actionLink: t('homepage:contact.form.actionShort'),
    isModalAction: true,
  },
  {
    id: "vyplnte-section-mobile",
    icon: "/assets/img/form_inaq.png",
    title: t('homepage:contact.formMobile.title'),
    description: t('homepage:contact.formMobile.description'),
    actionText: t('homepage:contact.formMobile.action'),
    actionLink: t('homepage:contact.formMobile.action'),
    isModalAction: true,
  },
];

const AnimatedLogo = () => {
  const { t } = useTranslation(['homepage']);
  const logoAltText = t('homepage:logoAlt');
  const logoRef = useRef(null);
  const tlRef = useRef(null);
  const animatedRef = useRef(false);

  useEffect(() => {
    // Initialize the animation timeline
    if (logoRef.current) {
      logoRef.current.style.opacity = "1";
      logoRef.current.style.visibility = "visible";
      // Check if ref exists
      const paths = logoRef.current.querySelectorAll("path, circle");

      // Set initial state
      paths.forEach((path) => {
        try {
          // Safely get path length
          const length = path.getTotalLength ? path.getTotalLength() : 100;
          gsap.set(path, {
            strokeDasharray: length,
            strokeDashoffset: length,
            fill: "transparent",
            stroke: "white",
            strokeWidth: 12,
          });
        } catch (error) {
          // Fallback for elements that don't support getTotalLength
          console.warn("Could not get length for SVG element:", error);
          gsap.set(path, {
            fill: "transparent",
            stroke: "white",
            strokeWidth: 12,
          });
        }
      });

      // Create timeline
      tlRef.current = gsap
        .timeline({ paused: true })
        .to(paths, {
          strokeDashoffset: 0,
          duration: 1.1,
          stagger: 0.2,
          ease: "power2.inOut",
        })
        .to(paths, {
          fill: "white",
          duration: 0.5,
          strokeWidth: 0,
          ease: "power1.inOut",
        });

      // Play the animation after 5 seconds
      const timer = setTimeout(() => {
        if (!animatedRef.current) {
          tlRef.current.play();
          animatedRef.current = true;
        }
      }, 1300);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleMouseEnter = () => {
    if (animatedRef.current && tlRef.current && !tlRef.current.isActive()) {
      // Reset to start before playing
      tlRef.current.progress(0);
      tlRef.current.play();
    }
  };

  // Add return statement to render the SVG
  return (
    <svg
      ref={logoRef}
      className='default-logo md:hidden'
      width='66'
      height='72'
      viewBox='180 60 617 800'
      aria-labelledby='iq-logo-title'
      role='img'
      onMouseEnter={handleMouseEnter}
      style={{ width: "66px", height: "72px", flex: "0 0 auto" }}
    >
      <title id='iq-logo-title'>{logoAltText}</title>
      <circle
        cx='432.68'
        cy='406.63'
        r='46.86'
        transform='translate(-38 768.59) rotate(-80.78)'
      />
      <path d='M479.54,520.07v138.23c0,.48-.01.95-.03,1.42-.74,25.22-21.43,45.44-46.83,45.44s-46.09-20.22-46.83-45.44c-.02-.47-.03-.94-.03-1.42v-138.23c0-25.88,20.98-46.86,46.86-46.86s46.86,20.98,46.86,46.86Z' />
      <path d='M590.1,507.34c.55,14.01,15.99,25.25,34.95,25.25s34.4-11.24,34.95-25.25c0-.26.02-.52.02-.79v-63.26c-.19-19.29-.38-36.95-.57-56.25,0-5.97-3.84-12.55-11.63-17.98-5.52-3.85-11.66-4.89-11.66-4.89-.04-.01-.08-.01-.12-.02-6.65-1.28-12.18-2.1-17.32-2.61h-63.7c-.28,0-.56,0-.84-.02h-.16c-12.87-.53-23.13-11.12-23.13-24.11v-52.96c0-13.33,11.2-21.91,24.52-21.91h17.48c13.11,0,23.74,10.63,23.74,23.74v14.76c0,11.65,9.45,21.1,21.1,21.1h19.08c12.82,0,23.21-10.39,23.21-23.21v-16.59c0-6.87-.56-11.52-2.22-17.87-8.8-33.7-39.77-54.63-76.23-54.63h-40.31c-43.33,0-78.46,35.13-78.46,78.46v53.67c19.47,6.11,35.71,25.67,38.2,47.37l.15,1.6c1.46,11.01,13.69,19.55,25.06,19.79l7.32-.09c44.8.19,56.25-3.61,56.45,14.19l.12,82.51Z' />
    </svg>
  );
};
const Homepage = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [showTypeAnimation, setShowTypeAnimation] = useState(true);

  // i18n hooks — use `ready` so we don't render menu items with raw keys
  // (e.g. "nav.aiOffer") before translations finish loading. Mirrors the
  // pattern used in src/pages/blog/Layout.jsx.
  const { t, ready } = useTranslation(['common', 'homepage'], { useSuspense: false });
  const { currentLang, getLocalizedPath } = useLanguage();

  // Generate translated header data (only when translations are ready)
  const headerData = ready ? generateHeaderData(t, getLocalizedPath) : [];
  const headerDataMobile = ready ? generateHeaderDataMobile(t, getLocalizedPath) : [];
  const contactData = generateContactData(t);
  const serviceData = generateServiceData(t);
  const jobData = generateJobData(t);
  const preloaderText = {
    marketingova: t('homepage:preloader.marketingova'),
    b2b: t('homepage:preloader.b2b'),
    agentura: t('homepage:preloader.agentura')
  };
  const logoAltText = t('homepage:logoAlt');

  const {
    posts: sanityPosts,
    loading: postsLoading,
    error: postsError,
  } = useSanityBlogPosts(6, currentLang);

  // Transform Sanity posts to homepage format
  const transformedSanityPosts = transformSanityToHomepageFormat(sanityPosts);

  useEffect(() => {
    // Simulate loading time and hide preloader
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1300); // Adjust time as needed

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Check if we need to scroll to a section
    const sectionId = sessionStorage.getItem("scrollToSection");
    if (sectionId) {
      // Clear the stored section ID
      sessionStorage.removeItem("scrollToSection");

      // Wait a bit for the page to fully render
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          // Using smooth scroll behavior
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 600); // Give it a short delay to ensure page is loaded
    }
  }, []);

  useEffect(() => {
    // Initialize cookie consent when homepage mounts
    console.log("Initializing cookie consent in Homepage");

    // Add a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initCookieConsent()
        .then((success) => {
          if (success) {
            console.log("Cookie consent successfully initialized in Homepage");
          } else {
            console.error("Failed to initialize cookie consent in Homepage");
          }
        })
        .catch((error) => {
          console.error(
            "Error initializing cookie consent in Homepage:",
            error
          );
        });
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Force TypeAnimation remount when language changes
  useEffect(() => {
    setShowTypeAnimation(false);
    const timer = setTimeout(() => {
      setShowTypeAnimation(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [currentLang]);

  useEffect(() => {
    // Check if we need to scroll to a section
    const sectionId = sessionStorage.getItem("scrollToSection");
    if (sectionId) {
      // Clear the stored section ID
      sessionStorage.removeItem("scrollToSection");

      // Wait a bit for the page to fully render
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          // Using smooth scroll behavior
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 500); // Increased timeout to ensure all content is loaded
    }
  }, []); // Empty dependency array means this runs once when component mounts

  // Also add this helper function at the top of your file with other imports
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const scrollToSection = (sectionId) => {
    if (window.location.pathname !== "/") {
      // If we're not on homepage, store the section ID and navigate
      sessionStorage.setItem("scrollToSection", sectionId);
      window.location.href = `/#${sectionId}`;
    } else {
      // If we're already on homepage, just scroll
      const element = document.getElementById(sectionId);
      element?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      // Using window dimensions for a larger tracking area
      const x = e.clientX - window.innerWidth / 2;
      const y = e.clientY - window.innerHeight / 2;
      setX(x);
      setY(y);
    };

    // Add listener to window instead of specific element
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <>
      <Helmet>
        <html lang={currentLang} />
        <title>{t('homepage:meta.title')}</title>
        <meta
          name='description'
          content={t('homepage:meta.description')}
        />
        <meta
          name='keywords'
          content={t('homepage:meta.keywords')}
        />
      </Helmet>
      <Preloader isLoading={isLoading} text={preloaderText} />
      <div className='inaqpage homepage cursor-default' style={props.style}>
        {/* Header Start */}
        <Header topSpace={{ md: true }} type='reverse-scroll'>
          <HeaderNav
            theme='light'
            expand='lg'
            containerClass='sm:!px-0 mx-auto w-full'
            containerStyle={{ maxWidth: "1280px" }}
            className='py-[0px] px-[35px] md:pr-[15px] md:pl-[10px] md:py-[12px]'
            fluid={false}
          >
            <Col sm={6} lg={2} xs='auto' className='me-auto ps-0'>
              <Link
                aria-label='logo home'
                className='flex items-center justify-start'
                to='/'
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <Navbar.Brand className='inline-block p-0 m-0 '>
                  <AnimatedLogo />
                  <svg
                    className='mobile-logo xxxl:hidden md:block ml-0 mr-auto '
                    width='78'
                    height='70.2'
                    viewBox='280 60 617 800'
                    aria-labelledby='iq-logo-mobile-title'
                    role='img'
                    style={{ width: "78px", height: "70.2px", flex: "0 0 auto" }}
                  >
                    <title id='iq-logo-mobile-title'>
                      {logoAltText}
                    </title>
                    <circle
                      cx='432.68'
                      cy='406.63'
                      r='46.86'
                      transform='translate(-38 768.59) rotate(-80.78)'
                      style={{ fill: "#fff" }}
                    />
                    <path
                      d='M479.54,520.07v138.23c0,.48-.01.95-.03,1.42-.74,25.22-21.43,45.44-46.83,45.44s-46.09-20.22-46.83-45.44c-.02-.47-.03-.94-.03-1.42v-138.23c0-25.88,20.98-46.86,46.86-46.86s46.86,20.98,46.86,46.86Z'
                      style={{ fill: "#fff" }}
                    />
                    <path
                      d='M590.1,507.34c.55,14.01,15.99,25.25,34.95,25.25s34.4-11.24,34.95-25.25c0-.26.02-.52.02-.79v-63.26c-.19-19.29-.38-36.95-.57-56.25,0-5.97-3.84-12.55-11.63-17.98-5.52-3.85-11.66-4.89-11.66-4.89-.04-.01-.08-.01-.12-.02-6.65-1.28-12.18-2.1-17.32-2.61h-63.7c-.28,0-.56,0-.84-.02h-.16c-12.87-.53-23.13-11.12-23.13-24.11v-52.96c0-13.33,11.2-21.91,24.52-21.91h17.48c13.11,0,23.74,10.63,23.74,23.74v14.76c0,11.65,9.45,21.1,21.1,21.1h19.08c12.82,0,23.21-10.39,23.21-23.21v-16.59c0-6.87-.56-11.52-2.22-17.87-8.8-33.7-39.77-54.63-76.23-54.63h-40.31c-43.33,0-78.46,35.13-78.46,78.46v53.67c19.47,6.11,35.71,25.67,38.2,47.37l.15,1.6c1.46,11.01,13.69,19.55,25.06,19.79l7.32-.09c44.8.19,56.25-3.61,56.45,14.19l.12,82.51Z'
                      style={{ fill: "#fff" }}
                    />
                  </svg>
                </Navbar.Brand>
              </Link>
            </Col>

            {/* The key is to NOT include the Navbar.Toggle here */}

            {/* Desktop Menu - Only visible on large screens */}
            <div className='col-auto justify-center menu-order inaq-header-menu px-lg-0 col-lg-7 d-none d-lg-block'>
              <Menu {...props} data={headerData} openModal={() => setIsModalOpen(true)} />
            </div>

            {/* Mobile Menu - This is where we properly implement it */}
            <MobileMenu data={headerDataMobile} openModal={() => setIsModalOpen(true)} />

            <Col xs='auto' lg={3} className='pe-0 d-flex align-items-center justify-content-end gap-3 flex-nowrap'>
              <LanguageSwitcher className='d-none d-lg-block' />
              <Buttons
                type='button'
                onClick={() => setIsModalOpen(true)}
                className='rounded-sm font-medium font-rubik uppercase shadow-[0_0_30px_rgba(0,0,0,0.08)] md:hidden hover:text-black whitespace-nowrap'
                themeColor={["#fff", "#fff"]}
                size='xs'
                backgroundColor='transparent'
                color='#212529'
                title={t('buttons.contact')}
              />
            </Col>
          </HeaderNav>
        </Header>
        {/* Header End */}
        <ModalPopupContact
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
        {/* Video Hero section start*/}
        <div className='hero-stack overflow-hidden relative'>
          <div
            className='hero-overlay lg-no-parallax bg-cover absolute top-[0px] left-0 w-full h-[95vh] md:h-[70vh] sm:h-[400px] z-0'
            style={{
              backgroundColor: "rgba(36, 16, 37, 1)",
            }}
          />
          <VideoBackground />
          <Container className='hero-content relative z-20 md:max-w-[860px]'>
            <Row className='items-center min-h-[85vh] sm:min-h-[80vh] justify-center pt-[92px] lg:pt-[84px] md:pt-[78px] sm:pt-[64px]'>
              <Col lg={8} md={8} sm={8}>
                <h1
                  className='font-["Oxanium"] text-[#fff] mb-[2.5rem] tracking-[-2px] sm:mb-[20px] xs:mb-[15px] text-center relative transform transition-transform duration-200 ease-out homepage-header text-[5rem] xxs:text-[44px]'
                  style={{
                    transform: `translate(${x / 60}px, ${y / 60}px)`,
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setX(e.clientX - rect.left - rect.width / 1);
                    setY(e.clientY - rect.top - rect.height / 1);
                  }}
                >
                  {t('homepage:hero.title')} <br />{t('homepage:hero.subtitle')}
                  {showTypeAnimation && (
                    <TypeAnimation
                      key={currentLang}
                      sequence={[
                        t('homepage:hero.typeAnimation.marketing'),
                        2000,
                        t('homepage:hero.typeAnimation.approach'),
                        2000,
                        t('homepage:hero.typeAnimation.knowHow'),
                        2000,
                      ]}
                      wrapper='div'
                      speed={40}
                      repeat={Infinity}
                      className='whitespace-pre-line'
                      style={{ fontStyle: "italic" }}
                    />
                  )}
                </h1>

                <p className='text-[20px] lg:text-[16px] font-rubik font-light mb-12 lg:w-[100%] md:w-full xs:mb-[32px] xs:px-4 text-center text-[#fff] opacity-90 leading-10'>
                  {t('homepage:hero.description')}
                </p>
                <ScrollTo
                  href='#services'
                  to='services'
                  offset={0}
                  delay={0}
                  spy={true}
                  smooth={true}
                  duration={800}
                  className='cursor-pointer flex justify-center w-full'
                >
                  <button className=' group relative inline-flex items-center justify-centertracking-[1px] font-[Oxanium] text-sm md:mb-[15px] sm:mt-8 text-white border-[1px] border-white rounded-[2px] font-medium transition-all duration-300 ease-in-out hover:bg-white hover:opacity-100 hover:text-[#6a604d] hover:shadow-[0_0_30px_rgba(0,0,0,0.08)]'>
                    <span className='relative hover:text-inaqpurple z-10 px-4 py-2'>
                      {t('homepage:hero.cta')}
                    </span>
                  </button>
                </ScrollTo>
              </Col>
            </Row>
            <ScrollTo
              to='services'
              offset={0}
              delay={0}
              spy={true}
              smooth={true}
              duration={800}
              className='w-full absolute bottom-[30px] left-0 right-0 z-20 flex justify-center items-center xs:hidden cursor-pointer'
              aria-label='Scroll to services section'
              role='button'
              tabIndex={0}
              href='#services'
            >
              <div className='scroll-icon ex-1'>
                <div className='wheel'></div>
              </div>
            </ScrollTo>
          </Container>
        </div>
        {/* Video Hero section start */}

        {/* Lazy Load HTML */}
        <InViewPort>
          {/* Section Start */}
          <m.section
            id='services'
            className='bg-lightgray py-[130px] overflow-hidden lg:py-[90px] md:py-[75px] sm:py-[50px] sm:px-4'
            {...fadeIn}
          >
            <Container>
              <Row className='justify-center'>
                <m.div
                  className='col col-md-10 text-center mb-20 sm:mb-12'
                  {...fadeIn}
                >
                  <h2 className='heading-5 font-["Oxanium"] text-[48px] sm:text-[36px] text-inaqpurple font-[500] tracking-[-2px] lg:leading-[46px]'>
                    {t('homepage:services.heading')}
                  </h2>
                  <span className='font-rubik text-[20px] lg:text-[16px] lg:leading-[28px] text-inaqgold inline-block mb-[15px] sm:mb-[10px] mx-8 lg:mx-0'>
                    {t('homepage:services.description')}
                  </span>
                </m.div>
              </Row>
              <Suspense
                fallback={<div className='loading-placeholder'>Loading...</div>}
              >
                <LazyServices
                  key={currentLang}
                  grid='row-cols-lg-3 row-cols-sm-2 row-cols-1 gap-y-20'
                  theme='service-inaq items-center'
                  data={serviceData}
                  animation={fadeIn}
                />
              </Suspense>
            </Container>
          </m.section>
          {/* Section End */}
          {/* Section Start */}
          <section
            className='py-[130px] overflow-hidden lg:py-[90px] md:py-[75px] sm:py-[50px] sm:px-4 bg-slate-200'
            id='blog'
          >
            <Container>
              <Row className='justify-center'>
                <m.div
                  className='col-12 col-md-10 text-center mb-16 sm:mb-8'
                  {...fadeIn}
                >
                  <h2 className='heading-5 font-["Oxanium"] text-[48px] sm:!text-[36px] text-inaqpurple font-[500] tracking-[-2px] lg:leading-[52px] '>
                    {t('homepage:blog.heading')}
                  </h2>

                  <span className='font-rubik text-[20px] lg:text-[16px] lg:leading-[28px] text-inaqgold opacity-80 inline-block mb-[15px] sm:mb-[10px] mx-8 lg:mx-0 font-light '>
                    {t('homepage:blog.description')}
                  </span>
                </m.div>
              </Row>
              {/* Sanity Blog Posts */} {/* Latest Blog Posts from Sanity */}
              {!postsLoading &&
                !postsError &&
                transformedSanityPosts.length > 0 && (
                  <div className='mb-8'>
                    <Row className='justify-center mb-8'>
                      <div className='col-12 text-center'></div>
                    </Row>
                    <BlogOverlayImg
                      pagination={false}
                      grid='isotope-grid blog-grid-3col md:blog-grid-2col xs:blog-grid-1col gutter-double-extra-large'
                      data={transformedSanityPosts}
                      filter={false}
                      overlay='#232323cc'
                    />
                  </div>
                )}
              {/* Loading state for Sanity posts */}
              {postsLoading && (
                <div className='mb-8'>
                  <Row className='justify-center mb-8'>
                    <div className='col-12 text-center'>
                      <h3 className='font-["Oxanium"] text-[32px] sm:text-[24px] text-white font-normal tracking-[-1px] mb-4'>
                        {t('homepage:blog.latestPosts')}
                      </h3>
                    </div>
                  </Row>
                  <div className='text-center text-white opacity-70'>
                    {t('homepage:blog.loading')}
                  </div>
                </div>
              )}
              {/* Error state for Sanity posts */}
              {postsError && (
                <div className='mb-8'>
                  <Row className='justify-center mb-8'>
                    <div className='col-12 text-center'>
                      <h3 className='font-["Oxanium"] text-[32px] sm:text-[24px] text-white font-normal tracking-[-1px] mb-4'>
                        {t('homepage:blog.latestPosts')}
                      </h3>
                    </div>
                  </Row>
                  <div className='text-center text-white opacity-70'>
                    {t('homepage:blog.error')} {postsError}
                  </div>
                </div>
              )}
              {/* hardcoded posts removed — now using 6 from Sanity above */}
              {false && <BlogOverlayImg
                pagination={false}
                grid='isotope-grid blog-grid-3col md:blog-grid-2col xs:blog-grid-1col gutter-double-extra-large'
                data={[
                  {
                    _id: "3",
                    slug: {
                      current:
                        "geo-optimalizacia-generativne-vyhladavace-umelej-inteligencie-chatgpt-perplexity",
                    },
                    category: [
                      {
                        title: "Predaj a optimalizácia",
                        slug: { current: "marketing" },
                      },
                    ],
                    img: "https://cdn.sanity.io/images/s7eia0gv/production/059163833768f35277182080e03b3216d037e21b-610x406.jpg",
                    title:
                      " GEO: optimalizácia pre generatívne vyhľadávače - návod, tipy, stragégia",
                    excerpt:
                      "Kým pri SEO sa snažíme dostať na vysoké pozície v rebríčkoch Google, pri GEO sa snažíme, aby generatívne vyhľadávače zahrnuli náš obsah do syntetizovaných odpovedí.",
                    body: [],
                    date: "2024-04-22",
                    readTime: 11,
                  },
                  {
                    _id: "2",
                    slug: {
                      current:
                        "mcp-protokol-claude-umela-inteligencia-anthropic-model-context",
                    },
                    category: [
                      {
                        title: "AI",
                        slug: { current: "trendy-v-b2b" },
                      },
                    ],
                    img: "https://storage.googleapis.com/inaq_sk_bg_video/blog3.avif",
                    title: "Pochopenie MCP: Protokol ako USB k AI aplikáciám",
                    excerpt:
                      "Jedným z predpokladov nástupu umelej inteligencie do života bežných ľudí a firiem je takzvaná agentická AI. Tá má výrazne zvýšiť využiteľnosť a zapojenie do nemalej časti oblastí bežného života. ",
                    body: [],
                    date: "2024-03-02",
                    readTime: 6,
                  },
                  {
                    _id: "1",
                    slug: {
                      current:
                        "vykonnostny-marketing-brand-budovanie-znacky-pomer",
                    },
                    category: [
                      { title: "Trendy v B2B", slug: { current: "ai" } },
                    ],
                    img: "https://storage.googleapis.com/inaq_sk_bg_video/blog1.avif",
                    title:
                      "Marketingový rozpočet sa preklápa do výkonnosti. A čo brand?",
                    excerpt:
                      "Prideľovanie marketingových rozpočtov nielen v B2B segmente je zložitým strategickým rozhodnutím. Otázka na marketingových a finančých riaditeľov je jasná: Aký je správny pomer výkonnostného marketingu a brandingu?",
                    body: [],
                    date: "2024-02-12",
                    readTime: 7,
                  },
                ]}
                filter={false}
                overlay='#232323cc'
              />}
            </Container>
            <div className='flex justify-center w-full mt-10'>
              <Link
                to='/blog'
                onClick={(e) => {
                  e.preventDefault();
                  window.open("/blog", "_blank", "noopener,noreferrer");
                }}
                className='cursor-pointer flex justify-center'
              >
                <button className='group relative inline-flex items-center justify-center tracking-[1px] font-[oxanium] text-sm md:mb-[15px] sm:mt-8 text-inaqgold border-[1px] border-inaqgold rounded-[2px] font-medium transition-all duration-300 ease-in-out hover:bg-inaqgold hover:opacity-100 '>
                  <span className='relative hover:text-white z-10 px-12 py-2'>
                    {t('homepage:blog.viewBlog')}
                  </span>
                </button>
              </Link>
            </div>
          </section>
          {/* Section End */}
          {/* Section Start */}
          <section
            id='pricelist'
            className='py-[220px] sm:py-[50px] overflow-hidden lg:py-[200px] sm:px-4 bg-inaqpurple'
          >
            <Container>
              <Row className='sm:text-center'>
                <Col md={5} className='sm:mb-24'>
                  <h2 className='mb-0 heading-5 font-["Oxanium"] text-[48px] sm:text-[36px]  text-white font-normal tracking-[-2px] !leading-[48px]'>
                    <span className='block' dangerouslySetInnerHTML={{ __html: t('homepage:pricelist.heading') }} />
                  </h2>
                </Col>
                <Col lg={{ span: 6, offset: 1 }} md={7} className='sm:mb-24'>
                  <Suspense
                    fallback={
                      <div className='loading-placeholder'>Loading...</div>
                    }
                  >
                    <LazyCounter
                      as='h3'
                      theme='counter-style-01'
                      grid='row-cols-1 row-cols-md-2 row-cols-sm-2 gap-y-[48px]'
                      className='text-white'
                      duration={6}
                      data={[
                        {
                          number: {
                            text: "70",
                            class:
                              "font-semibold !text-[#fff] opacity-100 font-[Oxanium] text-[36px] sm:text-[28px] mb-2",
                          },
                          title: t('homepage:pricelist.collaboration'),
                        },
                        {
                          number: {
                            text: "120",
                            class:
                              "font-semibold text-[36px] font-[Oxanium] sm:text-[28px] mb-2",
                          },
                          title: t('homepage:pricelist.consultation'),
                        },
                      ]}
                      postfix_sign='€'
                      animation={{
                        animate: { opacity: 1 },
                        initial: { opacity: 1 },
                      }}
                    />
                  </Suspense>
                </Col>
              </Row>
            </Container>
          </section>
          {/* Section End */}
          {/* Section Start*/}
          <m.section
            {...{ ...fadeIn, transition: { delay: 0.3 } }}
            id='careers'
            className='py-[80px] overflow-hidden lg:py-[120px] md:py-[95px] sm:py-[80px] xs:py-[50px] sm:px-4'
          >
            <Container>
              <Row className='items-center md:justify-center'>
                <Col lg={4} md={8} className='md:text-center md:mb-24'>
                  <h2 className='heading-5 font-["Oxanium"] text-[48px] sm:text-[36px] text-inaqpurple font-[500] tracking-[-2px] lg:leading-[46px]'>
                    {t('homepage:careers.heading')}
                  </h2>
                  <p className='font-rubik text-[20px] lg:text-[16px] lg:leading-[28px] text-inaqgold inline-block mb-[15px] sm:mb-[10px] sm:mx-4 md:mr-0 mr-12'>
                    {t('homepage:careers.description')}
                  </p>
                </Col>
                <Col lg={8} md={12}>
                  <Suspense
                    fallback={
                      <div className='loading-placeholder'>Loading...</div>
                    }
                  >
                    <LazyJobs
                      key={currentLang}
                      grid='row-cols-1 row-cols-md-2 justify-center text-left gap-y-15 md:gap-y-[45px] sm:mx-4'
                      theme='job-inaq'
                      data={jobData}
                      animation={fadeIn}
                      animationDelay={0.5}
                    />
                  </Suspense>
                </Col>
              </Row>
            </Container>
          </m.section>
          {/* Section End */}
          {/* Section Start */}
          <section
            className='py-[130px] overflow-hidden lg:py-[90px] md:py-[75px] sm:py-[50px] sm:px-4 bg-inaqpurple'
            id='newsletter'
          >
            <Container>
              <Row className='justify-center'>
                <m.div
                  className='col-12 col-md-10 text-center mb-16 sm:mb-8'
                  {...fadeIn}
                >
                  <h2 className='heading-5 font-["Oxanium"] text-[48px] sm:!text-[36px] text-white font-normal tracking-[-2px] lg:leading-[52px] '>
                    {t('homepage:newsletter.heading')} <br className='hidden md:block' />
                  </h2>

                  <span className='font-rubik text-[20px] lg:text-[16px] lg:leading-[28px] text-white opacity-80 inline-block mb-[15px] sm:mb-[10px] mx-8 lg:mx-0 font-light '>
                    {t('homepage:newsletter.description')}
                  </span>
                </m.div>
              </Row>
              <Suspense
                fallback={<div className='loading-placeholder'>Loading...</div>}
              >
                {" "}
                <LazySubscribeForm key={currentLang} />
              </Suspense>
            </Container>
          </section>
          {/* Section End */}
          {/* Section Start */}
          <m.section
            id='contact'
            className='bg-lightgray py-[130px] overflow-hidden lg:py-[90px] md:py-[75px] sm:py-[50px] sm:px-4'
            {...fadeIn}
          >
            <Container>
              <Row className='justify-center'>
                <m.div className='col col-md-12 text-center mb-0' {...fadeIn}>
                  <h2 className='heading-5 font-["Oxanium"] text-[48px] sm:text-[36px] text-inaqpurple font-[500] tracking-[-2px] lg:leading-[46px]'>
                    {t('homepage:contact.heading')}
                  </h2>
                  <span className='font-rubik text-[20px] lg:text-[16px] lg:leading-[28px] text-inaqgold inline-block mb-[15px] sm:mb-[10px]'>
                    {t('homepage:contact.description')}
                  </span>

                  <div className='flex flex-row mx-auto mt-16 xs:mt-4'>
                    <AstronautImage />

                    {/* Contact Options Section - below the image */}
                    <div className='w-full'>
                      <Suspense
                        fallback={
                          <div className='loading-placeholder'>Loading...</div>
                        }
                      >
                        <LazyFancyTextBox
                          key={currentLang}
                          data={contactData}
                          animation={{
                            initial: { opacity: 0, y: 30 },
                            animate: { opacity: 1, y: 0 },
                          }}
                          animationDelay={0.2}
                          onActionClick={() => setIsModalOpen(true)}
                          className='bg-lightgray'
                        />
                      </Suspense>
                    </div>
                  </div>
                </m.div>
              </Row>
            </Container>
          </m.section>{" "}
          {/* Section End */}
        </InViewPort>
        {/* Footer Start */}
        <Footer
          className='inaq-footer'
          theme='light'
          logo='/assets/img/webp/inaq_logo.svg'
          objectFit='contain'
          loading='lazy'
        >
          {/* Add this directly in your Footer for the Homepage */}
          <div
            className='cookie-settings-container'
            style={{ textAlign: "center", marginTop: "20px" }}
          >
            <button
              type='button'
              data-cc='show-preferencesModal'
              className='nav-link'
              style={{
                background: "transparent",
                border: "none",
                color: "white",
                cursor: "pointer",
              }}
            >
              Nastavenie súkromia
            </button>
          </div>
        </Footer>
        {/* Footer End */}
      </div>
    </>
  );
};

export default Homepage;
